import React, { useEffect, useState } from "react";
import athenaService from "../../../services/athenaService";
import { 
  Avatar, 
  Box, 
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Alert,
  TextField,
  Typography,
  useTheme,
  TablePagination,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from "@mui/material";
import { 
  Add as AddIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  ChatBubbleOutline as MessageIcon,
  HealthAndSafety as InsuranceIcon,
  Person as PersonIcon,
  WarningAmber as AlertIcon
} from "@mui/icons-material";
import "./Patients.css"; // Reuse the Patients CSS

function InsuranceManager() {
  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isViewingPatient, setIsViewingPatient] = useState(false);
  const [insuranceFormOpen, setInsuranceFormOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5); 
  const [totalPatients, setTotalPatients] = useState(0);
  const [departments, setDepartments] = useState([]);
  const [providers, setProviders] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('1'); // Default to department 1
  const [selectedProvider, setSelectedProvider] = useState('26');
  const [appointmentsData, setAppointmentsData] = useState([]);
  
  // Add state for locations (grouped departments)
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [departmentsByLocation, setDepartmentsByLocation] = useState({});
  
  // Insurance form fields
  const [insurancePackageId, setInsurancePackageId] = useState("");
  const [insuranceIdNumber, setInsuranceIdNumber] = useState("");
  const [sequenceNumber, setSequenceNumber] = useState(1); // Default to primary insurance
  const [policyNumber, setPolicyNumber] = useState("");
  const [policyHolderSex, setPolicyHolderSex] = useState("");
  const [policyHolderFirstName, setPolicyHolderFirstName] = useState("");
  const [policyHolderLastName, setPolicyHolderLastName] = useState("");
  const [relationshipId, setRelationshipId] = useState(1); // Default to self
  
  // Insurance package options based on real data - Updated to match AppointmentInsurance.js
  const [insurancePackages, setInsurancePackages] = useState([
    { id: 98546, name: "Medicare" },
    { id: 15310, name: "Blue Cross" },
    { id: 74, name: "Cigna" },
    { id: 2150, name: "Blue Shield" },
    { id: 23344, name: "Aetna" },
    { id: 660279, name: "Health Net" },
    { id: 4953, name: "United Healthcare" },
    { id: 655450, name: "Tricare" },
    { id: 581754, name: "Adventist Health" },
    { id: 19961, name: "USC Health Plan" },
    { id: 190449, name: "Optum Medical Group" },
    { id: 0, name: "Self Pay (No Insurance)" }
  ]);
  
  // Relationship options with correct IDs
  const relationships = [
    { id: 1, name: "Self" },
    { id: 2, name: "Spouse" },
    { id: 3, name: "Child" },
    { id: 4, name: "Other" }
  ];
  
  const theme = useTheme();

  // Load departments and providers on component mount
  useEffect(() => {
    loadDepartmentsAndProviders();
  }, []);
  
  // Load appointments when filters change
  useEffect(() => {
    getAppointments();
  }, [selectedLocation, selectedDepartment, selectedProvider, searchTerm, page, rowsPerPage]);
  
  // Function to load departments and providers
  const loadDepartmentsAndProviders = async () => {
    try {
      // Load departments
      const depts = await athenaService.getDepartments();
      
      // Filter out departments with empty providerlist
      const filteredDepts = depts.filter(dept => 
        dept.providerlist && dept.providerlist.length > 0
      );
      
      console.log('Departments with providers:', filteredDepts);
      setDepartments(filteredDepts || []);
      
      // Group departments by location (city)
      const deptsByLocation = {};
      const locationList = [];
      
      filteredDepts.forEach(dept => {
        const locationName = dept.city || 'Unknown';
        
        // Initialize location group if it doesn't exist
        if (!deptsByLocation[locationName]) {
          deptsByLocation[locationName] = [];
          locationList.push({
            name: locationName,
            departments: []
          });
        }
        
        // Add department to its location group
        deptsByLocation[locationName].push(dept);
        
        // Update the location list with this department
        const locationIndex = locationList.findIndex(loc => loc.name === locationName);
        if (locationIndex >= 0) {
          locationList[locationIndex].departments.push(dept);
        }
      });
      
      console.log("Departments grouped by location:", deptsByLocation);
      console.log("Location list:", locationList);
      
      setLocations(locationList);
      setDepartmentsByLocation(deptsByLocation);
      
      // Get default department (first department with providers)
      const defaultDept = filteredDepts.length > 0 ? filteredDepts[0] : null;
      
      if (defaultDept) {
        setSelectedDepartment(defaultDept.departmentid);
        
        // Load providers for the default department
        const provs = await athenaService.getProviders(parseInt(defaultDept.departmentid));
        
        // If department has a providerlist, filter providers to only include those in the list
        if (defaultDept.providerlist && defaultDept.providerlist.length > 0) {
          const filteredProvs = provs.filter(p => 
            defaultDept.providerlist.includes(p.providerid.toString())
          );
          console.log('Initial filtered providers for department:', filteredProvs);
          setProviders(filteredProvs || []);
        } else {
          // If no providerlist is available, use all providers
          setProviders(provs || []);
        }
      }
    } catch (error) {
      console.error("Error loading departments or providers:", error);
      setSnackbar({
        open: true,
        message: 'Failed to load departments or providers: ' + error.message,
        severity: 'error'
      });
    }
  };
  
  // Handle department change
  const handleDepartmentChange = async (event) => {
    try {
      const deptId = event.target.value;
      setSelectedDepartment(deptId);
      
      // Reset provider selection when department changes
      setSelectedProvider('');
      
      // Find the selected department from the departments list
      const selectedDept = departments.find(d => d.departmentid === deptId);
      
      if (selectedDept) {
        // Load providers for the selected department
        const provs = await athenaService.getProviders(parseInt(deptId));
        
        // If department has a providerlist, filter providers to only include those in the list
        if (selectedDept.providerlist && selectedDept.providerlist.length > 0) {
          const filteredProvs = provs.filter(p => 
            selectedDept.providerlist.includes(p.providerid.toString())
          );
          console.log('Filtered providers for department:', filteredProvs);
          setProviders(filteredProvs || []);
        } else {
          // If no providerlist is available, use all providers
          setProviders(provs || []);
        }
      }
    } catch (error) {
      console.error("Error loading providers:", error);
      setSnackbar({
        open: true,
        message: 'Failed to load providers: ' + error.message,
        severity: 'error'
      });
    }
  };
  
  // Handle location change
  const handleLocationChange = async (event) => {
    const location = event.target.value;
    console.log("Selected location:", location);
    
    setSelectedLocation(location);
    
    // If location is selected, get departments for this location
    if (location && departmentsByLocation[location]) {
      // Keep current department if it belongs to the selected location
      const deptInLocation = departmentsByLocation[location].find(
        dept => dept.departmentid.toString() === selectedDepartment
      );
      
      if (!deptInLocation) {
        // Reset department if current selection is not in this location
        setSelectedDepartment('');
      }
    }
  };

  // Handle provider change
  const handleProviderChange = (event) => {
    setSelectedProvider(event.target.value);
  };
  
  // Get appointments and extract patients without insurance
  const getAppointments = async () => {
    setLoading(true);
    try {
      // Get today's date
      const today = new Date();
      const startDate = new Date();
      const endDate = new Date();
      
      // Set date range to show upcoming appointments
      // Start with today's date
      startDate.setDate(today.getDate()-10);
      // End date is 30 days from now
      endDate.setDate(today.getDate() + 30);
      
      // Format dates as MM/DD/YYYY for athenaService
      const formatDate = (date) => {
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
      };
      
      let allAppointments = [];
      
      // If location is selected, fetch from all departments in that location
      if (selectedLocation && departmentsByLocation[selectedLocation]) {
        const locationDepts = departmentsByLocation[selectedLocation];
        
        // Fetch appointments for each department in the location
        for (const dept of locationDepts) {
          try {
            const deptAppointments = await athenaService.getBookedAppointments({
              departmentId: dept.departmentid,
              providerId: selectedProvider,
              startdate: formatDate(startDate),
              enddate: formatDate(endDate),
              showinsurance: true,
              showpatientdetail: true,
              showcancelled: false,
              limit: 100
            });
            
            if (deptAppointments && deptAppointments.length > 0) {
              allAppointments = [...allAppointments, ...deptAppointments];
            }
          } catch (error) {
            console.error(`Error fetching appointments for department ${dept.name}:`, error);
          }
        }
      } else {
        // Get appointments from athenaService (original behavior)
        allAppointments = await athenaService.getBookedAppointments({
          departmentId: selectedDepartment,
          providerId: selectedProvider,
          startdate: formatDate(startDate),
          enddate: formatDate(endDate),
          showinsurance: true,
          showpatientdetail: true,
          showcancelled: false,
          limit: 100 // get a good number of appointments
        });
      }
      
      console.log('Appointments data:', allAppointments);
      console.log('Number of appointments:', Array.isArray(allAppointments) ? allAppointments.length : 0);
      setAppointmentsData(allAppointments || []);
      
      // Process patients from appointments
      const patientsWithoutInsurance = [];
      const patientIds = new Set(); // Track unique patients
      
      if (Array.isArray(allAppointments)) {
        // First, collect unique patients from appointments
        for (const appointment of allAppointments) {
          if (appointment.patient && appointment.patientid && !patientIds.has(appointment.patientid)) {
            patientIds.add(appointment.patientid);
            
            // Apply search filter if search term exists
            const patientData = appointment.patient;
            const searchLower = searchTerm.toLowerCase();
            const patientFullName = `${patientData.firstname || ''} ${patientData.lastname || ''}`.toLowerCase();
            const patientEmail = (patientData.email || '').toLowerCase();
            const patientPhone = (patientData.mobilephone || patientData.homephone || '').toLowerCase();
            
            if (
              !searchTerm || 
              patientFullName.includes(searchLower) || 
              patientEmail.includes(searchLower) || 
              patientPhone.includes(searchLower)
            ) {
              // For each matching patient, fetch detailed information to check insurance
              try {
                // Get detailed patient info with insurance information
                const detailedPatientResponse = await athenaService.getPatientById(appointment.patientid, {
                  showinsurance: true
                });
                console.log(`Detailed patient info for ${appointment.patientid}:`, detailedPatientResponse);
                
                // The response is an array with one object containing patient data
                const detailedPatient = Array.isArray(detailedPatientResponse) && detailedPatientResponse.length > 0
                  ? detailedPatientResponse[0]
                  : detailedPatientResponse;
                
                // Check if patient has insurance
                console.log(`Patient ${appointment.patientid} (${patientData.firstname} ${patientData.lastname}) insurance check:`);
                console.log('- Insurance array exists:', !!detailedPatient.insurances);
                console.log('- Insurance is array:', Array.isArray(detailedPatient.insurances));
                console.log('- Insurance array length:', detailedPatient.insurances ? detailedPatient.insurances.length : 0);
                console.log('- Insurance data:', detailedPatient.insurances);
                
                // Only include patients with no insurance
                // We need to check if insurances exists as an array and has length > 0
                if (!detailedPatient.insurances || !Array.isArray(detailedPatient.insurances) || detailedPatient.insurances.length === 0) {
                  patientsWithoutInsurance.push({
                    patientid: appointment.patientid,
                    firstname: patientData.firstname,
                    lastname: patientData.lastname,
                    email: patientData.email,
                    mobilephone: patientData.mobilephone,
                    homephone: patientData.homephone,
                    sex: patientData.sex,
                    appointmentid: appointment.appointmentid,
                    appointmentdate: appointment.date,
                    dob: patientData.dob
                  });
                } else {
                  // If patient has insurance, log it for debugging
                  console.log(`Patient ${appointment.patientid} (${patientData.firstname} ${patientData.lastname}) has insurance and is excluded`);
                }
              } catch (error) {
                console.error(`Error getting patient details for ${appointment.patientid}:`, error);
                // If we can't check insurance status, include patient in the list
                patientsWithoutInsurance.push({
                  patientid: appointment.patientid,
                  firstname: patientData.firstname,
                  lastname: patientData.lastname,
                  email: patientData.email,
                  mobilephone: patientData.mobilephone,
                  homephone: patientData.homephone,
                  sex: patientData.sex,
                  appointmentid: appointment.appointmentid,
                  appointmentdate: appointment.date,
                  dob: patientData.dob,
                  errorCheckingInsurance: true
                });
              }
            }
          }
        }
      }
      
      // Store all filtered patients for total count
      setTotalPatients(patientsWithoutInsurance.length);
      
      // Apply pagination
      const startIndex = page * rowsPerPage;
      const paginatedPatients = patientsWithoutInsurance.slice(
        startIndex, 
        startIndex + rowsPerPage
      );
      
      // Set the paginated patients to display
      setPatients(paginatedPatients);
      
    } catch (error) {
      console.error("Error fetching appointments:", error);
      setSnackbar({
        open: true,
        message: 'Failed to load appointments: ' + error.message,
        severity: 'error'
      });
      setPatients([]);
      setTotalPatients(0);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };
  
  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Open the insurance form for a patient
  const handleAddInsurance = (patient) => {
    setSelectedPatient(patient);
    
    // Populate policy holder info from patient by default
    setPolicyHolderFirstName(patient.firstname || '');
    setPolicyHolderLastName(patient.lastname || '');
    setPolicyHolderSex(patient.sex || '');
    setRelationshipId(1); // Default to self
    
    setInsuranceFormOpen(true);
  };
  
  // Close the insurance form
  const handleCloseInsuranceForm = () => {
    setInsuranceFormOpen(false);
    setSelectedPatient(null);
    
    // Reset form fields
    setInsurancePackageId("");
    setInsuranceIdNumber("");
    setSequenceNumber(1);
    setPolicyNumber("");
    setPolicyHolderSex("");
    setPolicyHolderFirstName("");
    setPolicyHolderLastName("");
    setRelationshipId(1);
  };
  
  // Submit the insurance form
  const handleSubmitInsurance = async () => {
    if (!selectedPatient) {
      setSnackbar({
        open: true,
        message: 'No patient selected',
        severity: 'error'
      });
      return;
    }
    
    // Validate required fields
    if (!insurancePackageId) {
      setSnackbar({
        open: true,
        message: 'Please select an insurance provider',
        severity: 'error'
      });
      return;
    }
    
    // Skip validation for ID number and policy number if Self Pay is selected
    if (insurancePackageId !== 0 && !insuranceIdNumber) {
      setSnackbar({
        open: true,
        message: 'Please enter an insurance ID number',
        severity: 'error'
      });
      return;
    }
    
    if (insurancePackageId !== 0 && !policyNumber) {
      setSnackbar({
        open: true,
        message: 'Please enter a group/policy number',
        severity: 'error'
      });
      return;
    }
    
    if (!policyHolderFirstName || !policyHolderLastName) {
      setSnackbar({
        open: true,
        message: 'Please enter the policy holder\'s name',
        severity: 'error'
      });
      return;
    }
    
    if (!policyHolderSex) {
      setSnackbar({
        open: true,
        message: 'Please select the policy holder\'s sex',
        severity: 'error'
      });
      return;
    }
    
    setLoading(true);
    try {
      // Create the patient insurance
      await athenaService.createPatientInsurance(selectedPatient.patientid, {
        insurancepackageid: insurancePackageId,
        insuranceidnumber: insuranceIdNumber,
        sequencenumber: sequenceNumber,
        policynumber: policyNumber,
        insurancepolicyholdersex: policyHolderSex,
        insurancepolicyholderfirstname: policyHolderFirstName,
        insurancepolicyholderlastname: policyHolderLastName,
        relationshiptoinsuredid: relationshipId
      });
      
      // Show success message
      setSnackbar({
        open: true,
        message: `Insurance added successfully for ${selectedPatient.firstname} ${selectedPatient.lastname}`,
        severity: 'success'
      });
      
      // Close the form and refresh patient list
      handleCloseInsuranceForm();
      getAppointments();
    } catch (error) {
      console.error("Error creating insurance:", error);
      setSnackbar({
        open: true,
        message: 'Failed to add insurance: ' + error.message,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle snackbar close
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };
  
  // Get initials for avatar
  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };
  
  // Format date string from MM/DD/YYYY to a more readable format
  const formatAppointmentDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const [month, day, year] = dateString.split('/');
      return `${month}/${day}/${year}`;
    } catch (error) {
      return dateString;
    }
  };

  return (
    <Box sx={{ p: 0, height: '100vh', overflowY: 'auto' }}>
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          width: '100%', 
          p: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: theme.palette.background.paper,
          borderRadius: 0,
          borderBottom: `1px solid ${theme.palette.divider}`,
          mb: 0
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">Insurance Manager</Typography>
        </Box>
      </Paper>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          p: 3,
          bgcolor: theme.palette.background.paper,
          borderRadius: 0,
          borderBottom: `1px solid ${theme.palette.divider}`,
          mb: 3
        }}
      >
        <Typography variant="body1" sx={{ pb: 3 }}>
          Manage insurance information for patients without insurance or with outdated coverage.
        </Typography>
        
        {/* Filters and Search */}
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3} lg={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="location-label"
                shrink={true}
                >Location</InputLabel>
                <Select
                  labelId="location-label"
                  id="location-select"
                  value={selectedLocation}
                  label="Location"
                  onChange={handleLocationChange}
                  size="small"
                  displayEmpty
                  renderValue={(value) => value === "" ? "Default Location" : value}
                >
                  <MenuItem value="">Default Location</MenuItem>
                  {Object.keys(departmentsByLocation).map((locationName) => (
                    <MenuItem key={locationName} value={locationName}>
                      {locationName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid>
              <FormControl fullWidth size="small" sx={{ width: '100%', display: 'none'}}>
                <InputLabel id="department-label">Department</InputLabel>
                <Select
                  labelId="department-label"
                  id="department-select"
                  value={selectedDepartment}
                  label="Department"
                  onChange={handleDepartmentChange}
                >
                  {departments.map(dept => (
                    <MenuItem key={dept.departmentid} value={dept.departmentid}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3} lg={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="provider-label">Provider</InputLabel>
                <Select
                  labelId="provider-label"
                  id="provider-select"
                  value={selectedProvider}
                  label="Provider"
                  onChange={handleProviderChange}
                >
                  <MenuItem value="">All Providers</MenuItem>
                  {providers.map(prov => (
                    <MenuItem key={prov.providerid} value={prov.providerid}>
                      {prov.firstname} {prov.lastname}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3} lg={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by patient name, email, or phone number"
                value={searchTerm}
                onChange={handleSearchChange}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
            />
          </Grid>
        </Grid>
      </Box>
      </Paper>

      {/* Patient List */}
      <Box sx={{ px: 3 }}>
        <Paper sx={{ width: '100%' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : patients.length > 0 ? (
            patients.map((patient, index) => (
              <Paper 
                key={patient.patientid} 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  mb: 2, 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.02)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar 
                    sx={{ 
                      bgcolor: 'primary.main', 
                      width: 40, 
                      height: 40,
                      mr: 2 
                    }}
                  >
                    {getInitials(patient.firstname, patient.lastname)}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {`${patient.firstname} ${patient.lastname}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {patient.email || 'No email'} • {patient.mobilephone || patient.homephone || 'No phone'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Appointment Date: {formatAppointmentDate(patient.appointmentdate)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title={patient.errorCheckingInsurance ? 
                    "Could not verify insurance status" : 
                    "Patient has no insurance"
                  }>
                    <AlertIcon color={patient.errorCheckingInsurance ? "error" : "warning"} sx={{ mr: 1 }} />
                  </Tooltip>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<InsuranceIcon />}
                    onClick={() => handleAddInsurance(patient)}
                    sx={{
                      borderRadius: 4,
                      backgroundColor: '#383352',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#292639',
                      }
                    }}
                  >
                    Add Insurance
                  </Button>
                </Box>
              </Paper>
            ))
          ) : (
            <Typography variant="h6" align="center" sx={{ py: 4, color: theme.palette.text.secondary }}>
              {searchTerm ? 'No patients match your search criteria.' : 'No patients without insurance found.'}
            </Typography>
          )}
        </Paper>
      </Box>
      
      {/* Pagination */}
      {patients.length > 0 && (
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <TablePagination
            component="div"
            count={totalPatients}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Box>
      )}
      
      {/* Insurance Form Dialog */}
      <Dialog open={insuranceFormOpen} onClose={handleCloseInsuranceForm} fullWidth maxWidth="md">
        <DialogTitle>
          Add Insurance for {selectedPatient ? `${selectedPatient.firstname} ${selectedPatient.lastname}` : 'Patient'}
        </DialogTitle>
        <DialogContent>
          {selectedPatient && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel id="insurance-provider-label">Insurance Provider *</InputLabel>
                    <Select
                      labelId="insurance-provider-label"
                      id="insurance-provider"
                      value={insurancePackageId}
                      label="Insurance Provider *"
                      onChange={(e) => {
                        setInsurancePackageId(e.target.value);
                        // Clear fields if self-pay is selected
                        if (e.target.value === '0') {
                          setInsuranceIdNumber('');
                          setPolicyNumber('');
                          setPolicyHolderFirstName('');
                          setPolicyHolderLastName('');
                          setPolicyHolderSex('');
                        }
                      }}
                    >
                      <MenuItem value="" disabled>Select Insurance Provider</MenuItem>
                      {insurancePackages.map(pkg => (
                        <MenuItem key={pkg.id} value={pkg.id}>{pkg.name}</MenuItem>
                      ))}
                      <MenuItem value="0">No Insurance / Self Pay</MenuItem>
                    </Select>
                  </FormControl>
                
                  <TextField
                    label="Insurance ID Number *"
                    fullWidth
                    value={insuranceIdNumber}
                    onChange={(e) => setInsuranceIdNumber(e.target.value)}
                    placeholder="e.g., WT38920333"
                    sx={{ mb: 3 }}
                    required
                  />
                
                  <TextField
                    label="Group / Policy Number *"
                    fullWidth
                    value={policyNumber}
                    onChange={(e) => setPolicyNumber(e.target.value)}
                    placeholder="e.g., 34567899"
                    sx={{ mb: 3 }}
                    required
                  />
                
                  <FormControl fullWidth sx={{ mb: 3 }} required>
                    <InputLabel id="insurance-type-label">Insurance Type *</InputLabel>
                    <Select
                      labelId="insurance-type-label"
                      id="insurance-type"
                      value={sequenceNumber}
                      label="Insurance Type *"
                      onChange={(e) => setSequenceNumber(e.target.value)}
                      required
                    >
                      <MenuItem value={1}>Primary Insurance</MenuItem>
                      <MenuItem value={2}>Secondary Insurance</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Policy Holder Information</Typography>
                  
                  <FormControl fullWidth sx={{ mb: 3 }} required>
                    <InputLabel id="relationship-label">Relationship to Policy Holder *</InputLabel>
                    <Select
                      labelId="relationship-label"
                      id="relationship"
                      value={relationshipId}
                      label="Relationship to Policy Holder *"
                      onChange={(e) => setRelationshipId(e.target.value)}
                      required
                    >
                      {relationships.map(rel => (
                        <MenuItem key={rel.id} value={rel.id}>{rel.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <TextField
                    label="Policy Holder First Name *"
                    fullWidth
                    value={policyHolderFirstName}
                    onChange={(e) => setPolicyHolderFirstName(e.target.value)}
                    sx={{ mb: 3 }}
                    required
                  />
                  
                  <TextField
                    label="Policy Holder Last Name *"
                    fullWidth
                    value={policyHolderLastName}
                    onChange={(e) => setPolicyHolderLastName(e.target.value)}
                    sx={{ mb: 3 }}
                    required
                  />
                  
                  <FormControl fullWidth sx={{ mb: 3 }} required>
                    <InputLabel id="sex-label">Policy Holder Sex *</InputLabel>
                    <Select
                      labelId="sex-label"
                      id="sex"
                      value={policyHolderSex}
                      label="Policy Holder Sex *"
                      onChange={(e) => setPolicyHolderSex(e.target.value)}
                      required
                    >
                      <MenuItem value="" disabled>Select</MenuItem>
                      <MenuItem value="M">Male</MenuItem>
                      <MenuItem value="F">Female</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInsuranceForm}>Cancel</Button>
          <Button 
            onClick={handleSubmitInsurance} 
            variant="contained" 
            disabled={loading}
            sx={{
              bgcolor: '#383352',
              '&:hover': {
                bgcolor: '#292639',
              }
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Save Insurance'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default InsuranceManager;
